/**
 * Smart paste handler for <textarea> and <input> fields.
 * Reads the HTML flavor of the clipboard (if present) and converts
 * <p>, <br>, <li>, <div>, headings into real newlines so pasted content
 * from Google Docs / Word / browsers / email preserves paragraph structure.
 *
 * Usage: <Textarea onPaste={handleStructuredPaste} ... />
 */
export function handleStructuredPaste(e) {
  try {
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return; // fall through to default paste

    const html = cd.getData('text/html');
    if (!html || html.trim().length === 0) {
      return; // no HTML flavor — let default plain-text paste happen
    }

    // Convert HTML to newline-preserving plain text
    const text = htmlToStructuredText(html);
    if (!text) return;

    e.preventDefault();
    const el = e.target;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = el.value.slice(0, start);
    const after = el.value.slice(end);
    const newValue = before + text + after;

    // Use the native input value setter so React onChange fires
    const proto = el.tagName === 'TEXTAREA'
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (setter) {
      setter.call(el, newValue);
    } else {
      el.value = newValue;
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));

    // Position cursor after inserted text
    const caret = start + text.length;
    requestAnimationFrame(() => {
      try { el.setSelectionRange(caret, caret); } catch (_) { /* noop */ }
    });
  } catch (err) {
    // If anything goes wrong, let the default paste behavior take over
    // by not calling preventDefault
    // eslint-disable-next-line no-console
    console.warn('structured paste fallback', err);
  }
}

function htmlToStructuredText(html) {
  // Parse HTML in a sandboxed document so scripts don't run
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body || doc.documentElement;

  const BLOCK_TAGS = new Set([
    'p', 'div', 'section', 'article', 'header', 'footer',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'blockquote', 'pre', 'tr',
  ]);
  const LIST_ITEM = 'li';

  const chunks = [];

  function walk(node, ctx) {
    if (node.nodeType === Node.TEXT_NODE) {
      // Collapse whitespace runs but preserve single spaces
      const t = node.nodeValue.replace(/[ \t\f\v]+/g, ' ');
      chunks.push(t);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const tag = node.tagName.toLowerCase();

    if (tag === 'br') {
      chunks.push('\n');
      return;
    }
    if (tag === LIST_ITEM) {
      // Prefix with a dash so our backend PDF renderer recognizes it as a bullet
      chunks.push('\n- ');
      for (const child of node.childNodes) walk(child, ctx);
      return;
    }
    if (BLOCK_TAGS.has(tag)) {
      // Block start: paragraph break, unless we're at the start
      if (chunks.length && !chunks[chunks.length - 1].endsWith('\n\n')) {
        chunks.push('\n\n');
      }
      for (const child of node.childNodes) walk(child, ctx);
      // Block end: single newline (subsequent block will bump to double)
      if (chunks.length && !chunks[chunks.length - 1].endsWith('\n')) {
        chunks.push('\n');
      }
      return;
    }
    // Inline element — just recurse
    for (const child of node.childNodes) walk(child, ctx);
  }

  for (const child of body.childNodes) walk(child, {});

  let text = chunks.join('');
  // Normalize whitespace
  text = text.replace(/\u00a0/g, ' '); // nbsp -> space
  text = text.replace(/[ \t]+\n/g, '\n'); // trailing spaces before newline
  text = text.replace(/\n{3,}/g, '\n\n'); // collapse triple+ newlines
  text = text.trim();
  return text;
}
